import { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Paper,
  Avatar,
  Chip,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Link,
  useTheme,
  alpha,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
  AutoAwesome as AutoAwesomeIcon,
  Settings as SettingsIcon,
  CloudDone as CloudDoneIcon,
  TransferWithinAStation as TransferWithinAStationIcon,
  Chat as ChatIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import {
  Schedule as ScheduleIcon,
  EmojiEvents as AwardIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import {
  YouTube as YouTubeIcon,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import { httpRequest } from '../../api/helper';
import Copyright from '../../components/Copyright';
import AddModal from '../../components/AddModal';
import { useNotification } from '../../context/NotificationContext';
import MockTimeTable from './mock/MockTimeTable';
import { API_ERROR_MESSAGE, PRODUCT_NAME, YOUTUBE_CHANNEL_LINK, YOUTUBE_VIDEO_ID } from '../../common/AppConstants';
import * as AppUrls from '../../common/AppUrls';

const DEMO_API_URL = 'user/demo-request/';

const demoRequestColumns = [
  {
    attr: 'institute_name',
    editable: true,
    extra: false,
    label: 'Institution Name',
    related: false,
    required: true,
    type: 'text',
    help_text: 'Institution Name',
  },
  {
    attr: 'institute_type',
    editable: true,
    extra: false,
    label: 'Institution Type',
    related: true,
    required: true,
    type: 'choice',
    help_text: 'Institution Type',
  },
  {
    attr: 'contact_email',
    editable: true,
    extra: false,
    label: 'Email Address',
    related: false,
    required: true,
    type: 'text',
    help_text: 'Email Address',
  },
  {
    attr: 'contact_phone',
    editable: true,
    extra: false,
    label: 'Phone Number',
    related: false,
    required: false,
    type: 'text',
    help_text: 'Phone Number',
  },
  {
    attr: 'other_details',
    editable: true,
    extra: false,
    label: 'More details ?',
    related: false,
    required: false,
    type: 'text',
    help_text: 'Preferred time, or any other details you might want to share with us',
  },
];

const demoRequestRelatedData = {
  institute_type: {
    school: { label: "School" },
    college: { label: "College" },
    university: { label: "University" },
    training_center: { label: "Training Center" },
    other: { label: "Other" },
  }
}

const followUsItems = [
  {
    icon: <YouTubeIcon fontSize="small"/>,
    color: '#FF0000',
    path: YOUTUBE_CHANNEL_LINK,
    isExternal: true
  },
];

export default function Home() {
  const { addNotification } = useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const [isVisible, setIsVisible] = useState(true);
  const [activeFeature, setActiveFeature] = useState(0);

  const [isRequestDemoModalOpen, setIsRequestDemoModalOpen] = useState(false);

  // Animation effect on scroll
  // useEffect(() => {
  //   setIsVisible(true);
    
  //   // Rotate through features automatically
  //   const interval = setInterval(() => {
  //     setActiveFeature((prev) => (prev + 1) % features.length);
  //   }, 3000);
    
  //   return () => clearInterval(interval);
  // }, []);

  // Enhanced features with better descriptions and icons
  const features = [
    {
      title: 'Smart Scheduling',
      description: 'Auto generate TimeTables with AI-powered algorithms',
      icon: <AutoAwesomeIcon fontSize="large" />, // CalendarIcon
      gradient: `linear-gradient(135deg, #233836 30%, #0f1918 90%)`,
      feature_list: [
        {
          active: true,
          title: 'AI-based schedule generation'
        },
        {
          active: true,
          title: 'Automatically avoids conflicts'
        },
        {
          active: true,
          title: 'Guided onboarding and support'
        }
      ],
    },
    {
      title: 'Custom Rules Engine',
      description: 'Define teacher preferences, room constraints, and curriculum requirements effortlessly',
      icon: <SettingsIcon fontSize="large" />,  // BookIcon
      gradient: `linear-gradient(135deg, #1ABC9C 30%, #117A65 90%)`,
      feature_list: [
        {
          active: true,
          title: 'Manual class assignments'
        },
        {
          active: true,
          title: 'Flexible timing and break settings'
        },
        {
          active: true,
          title: 'Preference handling for teachers and rooms'
        }
      ],
    },
    {
      title: 'Staff Absence & Substitution',
      description: 'Easily manage teacher absences and auto-assign substitutes in seconds',
      icon: <TransferWithinAStationIcon fontSize="large" />, // or <SupervisorAccountIcon />
      gradient: `linear-gradient(135deg, #F5A623 0%, #D98200 100%)`,
      feature_list: [
        {
          active: true,
          title: 'Track teacher leave & unavailability'
        },
        {
          active: true,
          title: 'Auto-suggest substitute teachers'
        },
        {
          active: true,
          title: 'Instant timetable updates'
        }
      ],
    },
    {
      title: 'Real-Time Cloud Access',
      description: 'Access your schedules anywhere, share instantly, and collaborate with your team',
      icon: <CloudDoneIcon fontSize="large" />, // FlashOnIcon
      gradient: `linear-gradient(135deg, #4A90E2 0%, #0061C9 100%)`,
      feature_list: [
        {
          active: true,
          title: 'Unified cloud-based dashboard'
        },
        {
          active: true,
          title: 'Role-based views for students, teachers, and admins'
        },
        {
          active: true,
          title: 'Seamless Excel import and export'
        }
      ],
    },
    {
      title: 'Email & WhatsApp Communication',
      description: 'Send class updates, schedule changes, and announcements instantly',
      icon: <WhatsAppIcon fontSize="large" />,
      gradient: `linear-gradient(135deg, #2e7d32 30%, #1b5e20 90%)`,
      feature_list: [
        {
          active: true,
          title: 'Send timetable updates'
        },
        {
          active: true,
          title: 'WhatsApp + Email communication'
        },
        {
          active: true,
          title: 'Notification for substitutes'
        }
      ],
    },
  ];

  const handleDemoModalOpen = () => {
    setIsRequestDemoModalOpen(true);
  };

  const handleDemoModalClose = () => {
    setIsRequestDemoModalOpen(false);
  };

  const handleDemoSave = async (addFormData, onAddSuccess, onAddFailure) => {
    const response = await httpRequest({
      method: 'POST',
      url: DEMO_API_URL,
      data: addFormData,
      onSuccess: (data) => {
        handleDemoModalClose();
        onAddSuccess();
      },
      onFailure: (data) => {
        onAddFailure(data);
      },
      addNotification,
    });

    // Show request already received message
    if ((response.status >= 400 && response.status < 500) && response.data.demo_request?.[0]) {
      addNotification(response.data.demo_request?.[0], 'info');
      handleDemoModalClose();
    }
  };

  const handleFollowUsClick = (path, isExternal = false) => {
    if (path) {
      if (isExternal) {
        window.open(path, '_blank', 'noopener,noreferrer');
      } else {
        navigate(path);
      }
    }
  };

  // Testimonial data
  const testimonials = [
    {
      name: 'Sarah Johnson',
      role: 'Principal, Westfield High',
      quote: 'We reduced scheduling time by 85% and eliminated all conflicts. Best decision we made this year!',
      avatar: '/api/placeholder/48/48'
    },
    {
      name: 'Dr. Michael Chen',
      role: 'Dean, Pacific University',
      quote: 'The flexibility to handle our complex departmental requirements is impressive. Highly recommended.',
      avatar: '/api/placeholder/48/48'
    }
  ];

  // Stats with animation
  const stats = [
    { value: '95%', label: 'Time Saved' },
    { value: '100%', label: 'Conflict-Free' },
    { value: '5000+', label: 'Schools' }
  ];

  return (
    <>
      <Helmet>
        <title>ClassChronicle | TimeTable Creator for Schools, Colleges & Universities</title>
      </Helmet>
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'background.default',
        }}
      >
        {/* Hero Section with animated entry */}
        <Box component="header" id="header"
          sx={{
            pt: 10,
            pb: 10,
            px: 1.5,
            transition: 'all 1s ease',
            transform: isVisible ? 'translateY(0)' : 'translateY(50px)',
            opacity: isVisible ? 1 : 0,
            position: 'relative',
          }}
        >
          <Container maxWidth="xl" sx={{ textAlign: 'center' }}>
            <Chip 
              label="The Ultimate Educational Scheduling Platform"
              color="primary"
              variant="outlined"
              sx={{ mb: 4, py: 1, px: { xs: 0.5, sm: 2 }, '& .MuiChip-label': { px: { xs: 0.5, sm: 2 } } }}
            />
            
            <Typography
              variant="h1"
              gutterBottom
              sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 700,
                mb: 3,
                background: `linear-gradient(90deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.main})`,
                backgroundClip: 'text',
                textFillColor: 'transparent',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Intelligent Class Scheduling & TimeTable Management
            </Typography>
            
            <Typography 
              variant="h5" 
              color="text.secondary" 
              paragraph 
              sx={{ 
                maxWidth: '800px', 
                mx: 'auto', 
                mb: 5,
                fontSize: { xs: '1.1rem', md: '1.25rem' } 
              }}
            >
              Transform chaotic scheduling into streamlined efficiency. Create conflict-free and optimized timetables in just a few clicks.
            </Typography>
            
            <Stack 
              direction={{ xs: 'column', sm: 'row' }} 
              spacing={2}
              justifyContent="center"
              sx={{ mb: 8 }}
            >
              <Button 
                variant="contained" 
                color="primary"
                size="large"
                onClick={() => navigate('/sign-up')}
                sx={{ 
                  py: 1.5,
                  px: 4,
                  fontWeight: 600,
                  fontSize: '1rem',
                  boxShadow: 4,
                  '&:hover': {
                    boxShadow: 6,
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Try now
              </Button>
              <Button 
                variant="outlined" 
                color="primary"
                size="large"
                onClick={handleDemoModalOpen}
                sx={{ 
                  py: 1.5,
                  px: 4,
                  fontWeight: 600,
                  fontSize: '1rem',
                  borderWidth: 2,
                  '&:hover': {
                    borderWidth: 2,
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Get demo
              </Button>
            </Stack>

            {/* Floating dashboard preview */}
            <Paper
              elevation={12}
              sx={{
                position: 'relative',
                maxWidth: '1260px',  // Divisors of both 6 and 7 -> 1176, 1218, 1260, 1302
                height: { xs: '440px', md: '440px' }, // { xs: '290px', md: '440px' },
                mx: 'auto',
                // mb: 6,
                // background: `linear-gradient(45deg, ${theme.palette.grey[900]}, ${theme.palette.grey[800]})`,
                borderRadius: 4,
                // borderBottomRightRadius: '12px',
                overflow: 'hidden',
              }}
            >
              <MockTimeTable/>
            </Paper>
          </Container>
        </Box>

        {/* Feature Section with interactive cards */}
        <Box component="section" id="features"
          sx={{ py: 10, px: 1.5, bgcolor: 'background.paper' }}
        >
          <Container maxWidth="xl">
            <Typography
              variant="h2"
              align="center"
              sx={{
                mb: 1,
                fontWeight: 700,
                background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                backgroundClip: 'text',
                textFillColor: 'transparent',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Automated Scheduling Made Simple
            </Typography>
            <Typography
              variant="body1"  // h6
              align="center"
              color="text.secondary"
              paragraph
              sx={{ maxWidth: '800px', mx: 'auto', mb: 8 }}
            >
              Let our smart scheduling engine do all the heavy lifting and complex operations while you focus on what matters most.
            </Typography>

            <Grid container spacing={4}>
              {features.map((feature, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Card
                    sx={{
                      height: '100%',
                      boxShadow: activeFeature === index ? 8 : 3,
                      borderRadius: 4,
                      border: 'none',  // activeFeature === index ? `2px solid ${theme.palette.primary.light}` : 'none'
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'scale(1.03)',
                        boxShadow: 6,
                      },
                    }}
                    onMouseEnter={() => setActiveFeature(index)}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 2,
                        mb: 2,
                        backgroundImage: feature.gradient,
                        p: 4,
                        color: 'white',
                        alignItems: 'flex-start',
                      }}
                    >
                      <Box
                        sx={{
                          p: 1.5,
                          bgcolor: 'rgba(255, 255, 255, 0.2)',
                          borderRadius: 2,
                        }}
                      >
                        {feature.icon}
                      </Box>
                      <Box
                        sx={{
                          gap: 1,
                          mt: 0.5,
                        }}
                      >
                        <Typography variant="h5" fontWeight="bold" gutterBottom>
                          {feature.title}
                        </Typography>
                        <Typography variant="body1">{feature.description}</Typography>
                      </Box>
                    </Box>
                    <CardContent>
                      <List disablePadding>
                        {feature.feature_list.map((item) => (
                          <ListItem disablePadding key={item} sx={{ mb: 1 }}>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <CheckIcon color={ item.active ? "success": "secondary" } />
                            </ListItemIcon>
                            <ListItemText primary={item.title} />
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Stats section */}
        {/* <Box component="section" id="stats"
          sx={{ py: 8, bgcolor: theme.palette.primary.main, color: 'white' }}
        >
          <Container maxWidth="xl">
            <Grid container spacing={4}>
              {stats.map((stat, index) => (
                <Grid item xs={12} md={4} key={index} sx={{ textAlign: 'center' }}>
                  <Box sx={{ p: 3 }}>
                    <Typography variant="h3" fontWeight="bold" gutterBottom>
                      {stat.value}
                    </Typography>
                    <Typography variant="h6" sx={{ color: alpha(theme.palette.common.white, 0.8) }}>
                      {stat.label}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box> */}

        {/* Testimonials */}
        {/* <Box component="section" id="testimonials"
          sx={{ py: 10, px: 1.5, bgcolor: alpha(theme.palette.primary.light, 0.1) }}
        >
          <Container maxWidth="xl">
            <Typography variant="h3" fontWeight="bold" align="center" gutterBottom sx={{ mb: 8 }}>
              Trusted by Educators Worldwide
            </Typography>
            
            <Grid container spacing={4}>
              {testimonials.map((testimonial, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <Paper
                    elevation={3}
                    sx={{
                      p: 4,
                      borderRadius: 2,
                      height: '100%',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: 6,
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <Avatar
                        src={testimonial.avatar}
                        alt={testimonial.name}
                        sx={{ width: 56, height: 56, mr: 2 }}
                      />
                      <Box>
                        <Typography variant="h6" fontWeight="bold">
                          {testimonial.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {testimonial.role}
                        </Typography>
                      </Box>
                    </Box>
                    <Typography
                      variant="body1"
                      sx={{ fontStyle: 'italic', color: 'text.secondary' }}
                    >
                      "{testimonial.quote}"
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box> */}

        {/* Why Choose Us Section */}
        <Box component="section" id="whyclasschronicle"
          sx={{ py: 10, px: 1.5, bgcolor: 'background.paper' }}
        >
          <Container maxWidth="xl">
            <Typography variant="h3" fontWeight="bold" align="center" gutterBottom>
              Why Leading Educational Institutions Choose Us
            </Typography>
            <Typography
              variant="body1"  // h6
              align="center"
              color="text.secondary"
              paragraph
              sx={{ maxWidth: '800px', mx: 'auto', mb: 8 }}
            >
              We understand the unique challenges of academic scheduling and have built solutions
              specifically designed for excellence.
            </Typography>
            
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={6}>
                <Paper elevation={6} sx={{ borderRadius: 4, overflow: 'hidden' }}>
                  <Box
                    sx={{
                      position: 'relative',
                      paddingTop: '56.25%', // 16:9 aspect ratio
                    }}
                  >
                    <iframe
                      src={`https://www.youtube.com/embed/${YOUTUBE_VIDEO_ID}`}
                      title="Platform demonstration"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                      }}
                    />
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack spacing={4}>
                  <Box sx={{ display: 'flex', gap: 3 }}>
                    <Box sx={{ flexShrink: 0 }}>
                      <ScheduleIcon color="primary" sx={{ fontSize: 40 }} />
                    </Box>
                    <Box>
                      <Typography variant="h5" fontWeight="bold" gutterBottom>
                        Time-Saving Automation
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        Reduce scheduling time by up to 95% with our advanced algorithms that handle complex
                        constraints automatically.
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box sx={{ display: 'flex', gap: 3 }}>
                    <Box sx={{ flexShrink: 0 }}>
                      <AwardIcon color="primary" sx={{ fontSize: 40 }} />
                    </Box>
                    <Box>
                      <Typography variant="h5" fontWeight="bold" gutterBottom>
                        Educational Excellence
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        Built specifically for academic institutions with features that optimize learning
                        environments.
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box sx={{ display: 'flex', gap: 3 }}>
                    <Box sx={{ flexShrink: 0 }}>
                      <PeopleIcon color="primary" sx={{ fontSize: 40 }} />
                    </Box>
                    <Box>
                      <Typography variant="h5" fontWeight="bold" gutterBottom>
                        Stakeholder Satisfaction
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        Keep faculty, students, and administrators happy with schedules that respect preferences
                        and needs.
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* CTA Section */}
        <Box component="section" id="cta"
          sx={{
            py: 10,
            px: 1.5,
            background: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            color: 'white',
          }}
        >
          <Container maxWidth="md" sx={{ textAlign: 'center' }}>
            <Typography variant="h3" fontWeight="bold" gutterBottom>
              Ready to Transform Your Scheduling Process?
            </Typography>
            {/* <Typography variant="h6" paragraph sx={{ color: alpha(theme.palette.common.white, 0.8) }}>
              Join thousands of educational institutions already saving time and reducing stress.
            </Typography> */}
            
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent="center"
              marginTop={4}
            >
              <Button
                variant="contained"
                color="secondary"
                size="large"
                sx={{
                  bgcolor: 'white',
                  color: theme.palette.primary.main,
                  fontWeight: 600,
                  py: 1.5,
                  px: 4,
                  '&:hover': {
                    bgcolor: alpha(theme.palette.common.white, 0.9),
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease',
                }}
                onClick={() => navigate('/sign-up')}
              >
                Start My Free Trial
              </Button>
              <Button
                variant="outlined"
                size="large"
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  borderWidth: 2,
                  fontWeight: 600,
                  py: 1.5,
                  px: 4,
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: alpha(theme.palette.common.white, 0.1),
                    borderWidth: 2,
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease',
                }}
                onClick={handleDemoModalOpen}
              >
                Get a Demo
              </Button>
            </Stack>
          </Container>
        </Box>

        {/* Footer */}
        <Box component="footer" id="footer"
          sx={{
            py: 6,
            px: 1.5,
            bgcolor: theme.palette.primary.main,
            color: theme.palette.grey[200],
          }}
        >
          <Container maxWidth="xl">
            <Grid container spacing={4}>
              <Grid item xs={6} md={3}>
                <Typography variant="subtitle1" color="white" fontWeight="bold" gutterBottom>
                  Explore
                </Typography>
                <Stack spacing={1}>
                  <Typography variant="body2" component="a"
                    href="#header"
                    sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: 'white' } }}
                  >
                    Home
                  </Typography>
                  <Typography variant="body2" component="a"
                    href={AppUrls.DASHBOARD_URL}
                    sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: 'white' } }}
                  >
                    Dashboard
                  </Typography>
                  <Typography variant="body2" component="a"
                    href={AppUrls.PROFILE_URL}
                    sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: 'white' } }}
                  >
                    My Account
                  </Typography>
                  <Typography variant="body2" component="a"
                    href="#features"
                    sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: 'white' } }}
                  >
                    Features
                  </Typography>
                  {/* <Typography variant="body2" component="a"
                    href="/support"
                    sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: 'white' } }}
                  >
                    Support
                  </Typography> */}
                  {/* <Typography variant="body2" component="a"
                    href="#"
                    sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: 'white' } }}
                  >
                    Pricing
                  </Typography> */}
                  {/* <Typography variant="body2" component="a"
                    href="#"
                    sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: 'white' } }}
                  >
                    Case Studies
                  </Typography> */}
                </Stack>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="subtitle1" color="white" fontWeight="bold" gutterBottom>
                  Resources
                </Typography>
                <Stack spacing={1}>
                  <Typography variant="body2" component="a"
                    href="/docs"
                    sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: 'white' } }}
                  >
                    Getting Started
                  </Typography>
                  <Typography variant="body2" component="a"
                    href="/docs" target="_blank" rel="noopener noreferrer"
                    sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: 'white' } }}
                  >
                    Documentation
                  </Typography>
                  <Typography variant="body2" component="a"
                    href={YOUTUBE_CHANNEL_LINK} target="_blank" rel="noopener noreferrer"
                    sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: 'white' } }}
                  >
                    Video tutorials
                  </Typography>
                  <Typography variant="body2" component="a"
                    href="/support" target="_blank" rel="noopener noreferrer"
                    sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: 'white' } }}
                  >
                    Help Center
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="subtitle1" color="white" fontWeight="bold" gutterBottom>
                  Policies
                </Typography>
                <Stack spacing={1}>
                  <Typography variant="body2" component="a"
                    href="/terms-of-service" target="_blank" rel="noopener noreferrer"
                    sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: 'white' } }}
                  >
                    Terms of Service
                  </Typography>
                  <Typography variant="body2" component="a"
                    href="/privacy-policy" target="_blank" rel="noopener noreferrer"
                    sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: 'white' } }}
                  >
                    Privacy Policy
                  </Typography>
                  <Typography variant="body2" component="a"
                    href="/refund-policy" target="_blank" rel="noopener noreferrer"
                    sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: 'white' } }}
                  >
                    Refund Policy
                  </Typography>
                  <Typography variant="body2" component="a"
                    href="/shipping-policy" target="_blank" rel="noopener noreferrer"
                    sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: 'white' } }}
                  >
                    Shipping Policy
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} md={3}>
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                  <Box
                    component="img"
                    src="/logo192_light.png"
                    alt="ClassChronicle Logo"
                    sx={{ width: 48, height: 40, borderRadius: '8px' }}
                  />
                  <Typography variant="h3" color="white" fontWeight="bold" gutterBottom>
                    {PRODUCT_NAME}
                  </Typography>
                </Stack>
                <Typography variant="body2">
                  Transforming academic scheduling with intelligent solutions.
                </Typography>

                <Typography variant="h5" fontWeight="bold" mt="12px">
                  Follow us
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                  {followUsItems.map((item, index) =>
                    <Box
                      onClick={() => handleFollowUsClick(item.path, item.isExternal)}
                      sx={{
                        backgroundColor: '#FFFFFF',
                        color: item.color,
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        boxShadow: 1,
                      }}
                    >
                      {item.icon}
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>
            <Box sx={{ mt: 6, pt: 3, borderTop: `1px solid ${theme.palette.grey[700]}`, textAlign: 'center' }}>
              <Copyright sx={{ color: theme.palette.grey[200] }} />
            </Box>
          </Container>
        </Box>

        {/* Demo request Modal */}
        <Box>
          <AddModal
            open={isRequestDemoModalOpen}
            columns={demoRequestColumns}
            relatedData={demoRequestRelatedData}
            rows={[]}
            handleClose={handleDemoModalClose}
            handleSave={handleDemoSave}
            add_title="Get demo"
          />
        </Box>
      </Box>
    </>
  );
}
